import { createFileRoute, Navigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { handleGetStudyStudiesStudyIdGetOptions } from '@/api/generated/@tanstack/react-query.gen';
import { HandCalcGraphsProvider } from '@/hooks/handCalcGraphs';
import { useStudyDesignPoints } from '@/hooks/useStudyDesignPoints';
import { useAuth } from '@/hooks/useAuth';
import { useNoIndex } from '@/hooks/useNoIndex';
import { useRecordRecentView } from '@/hooks/useRecordRecentView';
import { CenteredSpinner } from '@/components/CenteredSpinner';
import {
  AccessDeniedScreen,
  ShareLoadErrorScreen,
} from '@/components/share/AccessDeniedScreen';
import { ReadOnlyBanner } from '@/components/share/ReadOnlyBanner';
import Report from '@/routes/_app/simops/-components/report/Report';
import { ReportSurfaceProvider } from '@/routes/_app/simops/-components/report/reportSurface';
import {
  shareLandingStepSlug,
  statusToStepSlug,
} from '@/routes/_app/simops/-components/meshing/status';
import { hasInspectableDesignPoint } from '@/routes/_app/simops/-data/design-point-results';
import {
  canAutoForwardReadOnly,
  canEditStudy,
  isAccessDeniedError,
  isKnownSignedInRole,
  readOnlyBannerRole,
} from '@/utils/shareAccess';

/**
 * Read-only public study report (ENG-1067) — the same `Report` component the
 * in-app Completed view renders, reused as-is: `Report` already derives
 * `canComment`/`isAuthenticated` from `study.my_role` and `useAuth()`
 * internally (ENG-1069), so mounting it here needs no read-only variant.
 * `Report` has no edit affordances of its own (it's a rendered report, not
 * an editor), so no extra gating is needed beyond what it already does for
 * comments.
 */
function StudyShareRoute() {
  const { studyId } = useParams({ from: '/share/study/$studyId' });
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  useNoIndex();

  const {
    data: study,
    isLoading,
    isError,
    error,
  } = useQuery({
    ...handleGetStudyStudiesStudyIdGetOptions({ path: { study_id: studyId } }),
    retry: false,
  });

  const { items: designPoints, query: designPointsQuery } =
    useStudyDesignPoints(studyId, {
      enabled: study != null && study.status !== 'draft',
    });

  useRecordRecentView({
    kind: 'study',
    id: studyId,
    name: study?.name,
    status: study?.status,
    myRole: study?.my_role,
  });

  // Auth SDK settles asynchronously (Stytch flips `isInitialized` in a
  // passive effect). Rendering with `isAuthenticated: false` while it
  // hydrates classifies a live viewer / commenter session as anonymous,
  // so the Open-in-App banner drops and the ReadOnlyBanner falls back
  // to the "You have view-only access" copy meant for signed-out
  // visitors. Wait for the SDK before painting either banner.
  if (isLoading || isAuthLoading) {
    return <CenteredSpinner />;
  }

  if (isError) {
    return isAccessDeniedError(error) ? (
      <AccessDeniedScreen />
    ) : (
      <ShareLoadErrorScreen />
    );
  }

  if (!study) {
    return <AccessDeniedScreen />;
  }

  // Signed-in users with any known role skip the intermediate share
  // Report entirely — they get bounced straight into the in-app
  // `/simops/$studyId` workbench. The `_app` layout mounts ActivityBar
  // for the sidebar, and every mutating action is already gated per
  // role by `canEditStudy`, so read-only tiers land on the full
  // workbench (mode rail + DP dashboard tree) with only the buttons
  // they're allowed to fire.
  //
  // Owners + editors auto-forward on every phase. Read-only tiers only
  // auto-forward when the target won't bounce them back: `_app/simops`
  // still redirects viewer/commenter on the `READ_ONLY_BLOCKED_PHASES`
  // (`draft` — study-wide authoring not yet per-button gated). Auto-
  // forwarding into that redirect would
  // ping-pong; `canAutoForwardReadOnly` mirrors the phase list so the
  // two files can't drift.
  if (isKnownSignedInRole(isAuthenticated, study)) {
    const forwardSafe =
      canEditStudy(study) || canAutoForwardReadOnly(study.status);
    if (forwardSafe) {
      // A study with no completed run lands on the step its status belongs
      // to rather than on Results, whose "not ready" placeholder is a dead
      // end (ENG-2858) — see `shareLandingStepSlug`.
      const hasResults = hasInspectableDesignPoint(designPoints);
      // Only a study whose own step is not Results waits on the points, since
      // only there can they change the answer. Do not mistake the hook's
      // initial empty array (or a page walk still in flight) for "no
      // results": a completed point on page 3 is still a result. Drafts never
      // enable the query, so a disabled query's `isPending` would spin forever.
      const isCheckingForResults =
        !hasResults &&
        study.status !== 'draft' &&
        statusToStepSlug(study.status) !== 'results' &&
        (designPointsQuery.isPending ||
          designPointsQuery.hasNextPage ||
          designPointsQuery.isFetchingNextPage);
      if (isCheckingForResults) return <CenteredSpinner />;

      // Always a step path, never the bare `/simops/$studyId`: StudyHeader
      // keys off `isResultsPath(pathname)` to decide whether to mount
      // `AnalyzeHeaderControls`, so a reader on the bare URL of a finished
      // study never sees the Overview | Points tab picker and cannot leave
      // Overview. The bare Results step redirects on to `/results/overview`,
      // which is the view a forwarded reader should land on.
      const step = shareLandingStepSlug(study.status, hasResults);
      return (
        <Navigate
          to={`/simops/$studyId/${step}`}
          params={{ studyId }}
          replace
        />
      );
    }
  }

  const bannerRole = readOnlyBannerRole(isAuthenticated, study.my_role);

  // Anonymous fallback — the read-only Report + Sign-in / Preferences buttons
  // in the banner. Same surface a public link would land on.
  //
  // `layout="grid"` is the Overview a signed-in reader sees (ENG-2367). Both
  // layouts render the SAME section list — `Report` builds it once and
  // branches only on placement — so this is not a second copy of the page; it
  // is the stacked document swapped for the dashboard the verdict leads.
  //
  // Without it a share link opened the document while every signed-in reader
  // was auto-forwarded to the dashboard, so the two audiences discussed the
  // same study through different front pages: the shared reader had to reach
  // §4 before learning whether the run was trustworthy at all.
  //
  // The grid's interactive affordances are each gated on a callback this
  // route does not pass (`onOpenDp`, `onOpenPoints`), so the dead ends stay
  // unrendered rather than appearing and doing nothing — an anonymous reader
  // has nowhere to navigate to. Widened to max-w-7xl because the dashboard is
  // a grid of cards; 5xl is the measure for a column of prose.
  return (
    <div className="min-h-screen bg-muted">
      {bannerRole && <ReadOnlyBanner role={bannerRole} ownerEmail={null} />}
      <div className="mx-auto max-w-7xl px-6 py-8">
        {/* What this surface cannot do, stated once. `?dp=` navigation needs
            a route that declares the param (only `_app/simops/$studyId`
            does), and the write-up drawer is mounted by `ResultsLayout`,
            which this route does not render. Without this the grid offered
            four links that rewrote the URL and did nothing, and a button
            with no drawer to open. */}
        <ReportSurfaceProvider
          value={{ canOpenDp: false, canOpenWriteup: false }}
        >
          {/* The report's hand-calc table reads each saved record through the
              read that never drafts (ENG-2861): a link recipient sees what an
              editor saved, and a point nobody filed says so. */}
          <HandCalcGraphsProvider
            studyId={study.id}
            designPoints={designPoints}
            readSaved
          >
            <Report study={study} designPoints={designPoints} layout="grid" />
          </HandCalcGraphsProvider>
        </ReportSurfaceProvider>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/share/study/$studyId')({
  component: StudyShareRoute,
});
